/**
 * Step 14 — Paperwork generation + signature (on-device or send-ARM) + ARM auto-upload.
 */
import { useState, useRef } from "react";
import SignaturePad from "react-signature-canvas";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Smartphone, Send, Loader2, RotateCcw, CheckCircle2, FileDown } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ARM_AFTER_SIGN_MSG =
  "Thank you for signing. Your full detailed estimate will be uploaded to your Apex Response Management account within 1 business day. " +
  "A manager will review your technician's assessment before work can begin. You will receive a notification when your estimate is ready for review and approval.";

export default function Step14Paperwork({ workflow, request, requestId, onAdvance, fetchWorkflow }) {
  const s14 = workflow?.step_14 || {};
  const s2 = workflow?.step_2 || {};
  const docType = s14.doc_type || (s2.payment_type === "Insurance" ? "work_auth" : "terms_of_service");
  const docLabel = docType === "work_auth" ? "Work Authorization" : "Terms of Service";

  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(!!s14.pdf_url);
  const [pdfUrl, setPdfUrl] = useState(s14.pdf_url || null);
  const [mode, setMode] = useState(s14.method || null);
  const [signerName, setSignerName] = useState(s14.signer_name || s2.full_name || request?.customer_name || "");
  const [submitting, setSubmitting] = useState(false);
  const [postSignDialog, setPostSignDialog] = useState(false);
  const sigRef = useRef(null);

  const signed = !!s14.signed_at;

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await API.post(`/inspection/${requestId}/step/14/generate`);
      setPdfUrl(data.pdf_url);
      setGenerated(true);
      toast.success(`${data.doc_type === "work_auth" ? "Work Authorization" : "Terms of Service"} generated`);
      fetchWorkflow();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not generate"); }
    finally { setGenerating(false); }
  };

  const submitOnDevice = async () => {
    if (!signerName.trim()) return toast.error("Enter signer name");
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error("Please sign in the box");
    setSubmitting(true);
    try {
      const data_url = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
      await API.post(`/inspection/${requestId}/step/14/sign`, {
        method: "on_device", signer_name: signerName.trim(), signature_data_url: data_url,
      });
      setPostSignDialog(true);
      fetchWorkflow();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not sign"); }
    finally { setSubmitting(false); }
  };

  const submitSendARM = async () => {
    if (!signerName.trim()) return toast.error("Enter signer name");
    setSubmitting(true);
    try {
      await API.post(`/inspection/${requestId}/step/14/sign`, {
        method: "send_arm", signer_name: signerName.trim(),
      });
      toast.success("Sent to customer's ARM app for signing");
      onAdvance(15);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4" data-testid="step-14-form">
      <div className="flex items-center gap-2">
        <FileText className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 14 · Paperwork & Signature</h2>
        <Badge className="ml-auto bg-zinc-100 text-zinc-700">{docLabel}</Badge>
      </div>

      {/* Generate paperwork */}
      {!generated && (
        <Card><CardContent className="pt-6 pb-6 text-center">
          <FileText className="w-10 h-10 text-amber-400 mx-auto mb-2" />
          <p className="text-sm text-zinc-700 font-semibold">Generate {docLabel}</p>
          <p className="text-xs text-zinc-500 mt-1 mb-4">
            Auto-fills customer name, address, scope, single-price total, date, and tech.
          </p>
          <Button onClick={generate} disabled={generating}
                  className="h-12 bg-zinc-900 hover:bg-zinc-800 text-white"
                  data-testid="step-14-generate">
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
              : "Generate Document"}
          </Button>
        </CardContent></Card>
      )}

      {generated && pdfUrl && (
        <Card>
          <CardContent className="pt-3 pb-3 flex items-center justify-between">
            <p className="text-xs font-semibold">{docLabel} ready</p>
            <Button size="sm" variant="outline" onClick={() => window.open(`${API_BASE}${pdfUrl}`, "_blank")}
                    data-testid="step-14-view-pdf">
              <FileDown className="w-3.5 h-3.5 mr-1" /> View
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Signing options */}
      {generated && !signed && !mode && (
        <div className="space-y-2" data-testid="step-14-method-select">
          <Button onClick={() => setMode("on_device")} className="w-full h-14 bg-zinc-900 text-white"
                  data-testid="step-14-on-device">
            <Smartphone className="w-5 h-5 mr-2" /> Customer Signs on This Device
          </Button>
          <Button onClick={() => setMode("send_arm")} variant="outline" className="w-full h-14"
                  data-testid="step-14-send-arm">
            <Send className="w-5 h-5 mr-2" /> Send to Customer's ARM App
          </Button>
        </div>
      )}

      {generated && !signed && mode === "on_device" && (
        <Card><CardContent className="pt-4 space-y-3">
          <Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Signer name"
                 className="h-11" data-testid="step-14-signer-name" />
          <div className="border-2 border-dashed border-zinc-300 rounded-lg bg-white">
            <SignaturePad ref={sigRef}
                          canvasProps={{ className: "w-full h-40 rounded-lg", "data-testid": "step-14-sig-pad" }} />
          </div>
          <Button onClick={() => sigRef.current?.clear()} variant="ghost" size="sm" data-testid="step-14-sig-clear">
            <RotateCcw className="w-3 h-3 mr-1" /> Clear
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => setMode(null)} variant="outline" className="h-11" data-testid="step-14-back-on-device">Back</Button>
            <Button onClick={submitOnDevice} disabled={submitting} className="h-11 bg-emerald-600 text-white"
                    data-testid="step-14-submit-on-device">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Signature"}
            </Button>
          </div>
        </CardContent></Card>
      )}

      {generated && !signed && mode === "send_arm" && (
        <Card><CardContent className="pt-4 space-y-3">
          <p className="text-xs text-zinc-600">
            We'll send a secure signing link via SMS to <strong>{request?.customer_phone || "the customer"}</strong>
            {request?.homeowner_fcm_token ? ", and a push notification to their ARM app." : "."}
          </p>
          <Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Signer name on record"
                 className="h-11" data-testid="step-14-signer-name-arm" />
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => setMode(null)} variant="outline" className="h-11" data-testid="step-14-back-arm">Back</Button>
            <Button onClick={submitSendARM} disabled={submitting} className="h-11 bg-zinc-900 text-white"
                    data-testid="step-14-submit-send-arm">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Link"}
            </Button>
          </div>
        </CardContent></Card>
      )}

      {signed && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4 text-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-1" />
            <p className="text-xs font-semibold text-zinc-900">
              Signed by {s14.signer_name} at {new Date(s14.signed_at).toLocaleString()}
            </p>
            {s14.signed_pdf_url && (
              <Button size="sm" variant="outline" className="mt-2"
                      onClick={() => window.open(`${API_BASE}${s14.signed_pdf_url}`, "_blank")}
                      data-testid="step-14-view-signed">
                <FileDown className="w-3 h-3 mr-1" /> View Signed PDF
              </Button>
            )}
            <Button onClick={() => onAdvance(15)} className="mt-3 h-11 bg-zinc-900 text-white block w-full"
                    data-testid="step-14-next">
              Continue to Sign-off
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Post-sign acknowledgement popup */}
      {postSignDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
             data-testid="step-14-post-sign-dialog">
          <Card className="w-full max-w-sm">
            <CardContent className="pt-5 space-y-3 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
              <h3 className="text-sm font-bold text-zinc-900">Thank you for signing</h3>
              <p className="text-xs text-zinc-600 leading-relaxed">{ARM_AFTER_SIGN_MSG}</p>
              <Button onClick={() => { setPostSignDialog(false); onAdvance(15); }}
                      className="w-full h-11 bg-zinc-900 text-white" data-testid="step-14-post-sign-continue">
                Continue
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
